/* Current plan prices + receiving bank accounts for the admin settings page:
 * GET /admin/plans and GET /admin/bank-accounts (useAdminPlans / useAdminBankAccounts).
 * The PUT responses are authoritative and are written straight into the same query cache. */
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { qk, useAdminBankAccounts, useAdminPlans } from "../../data/hooks";
import type { BankAccount } from "../../data/types";

export interface AdminSettingsData { planPrices: Record<string, number>; bankAccounts: BankAccount[] }

/** Both reads combined into one query-like object (for `<Loaded>`). */
export function useAdminSettingsData() {
  const plans = useAdminPlans();
  const banks = useAdminBankAccounts();
  const data: AdminSettingsData | undefined = plans.data && banks.data ? { planPrices: plans.data, bankAccounts: banks.data } : undefined;
  return {
    data,
    isLoading: plans.isLoading || banks.isLoading,
    error: plans.error ?? banks.error ?? null,
    refetch: () => Promise.all([plans.refetch(), banks.refetch()]),
  };
}

/** Store an authoritative server response (prices and/or accounts). */
export function useStoreAdminSettings() {
  const qc = useQueryClient();
  return useCallback((patch: Partial<AdminSettingsData>) => {
    if (patch.planPrices) qc.setQueryData(qk.adminPlans, patch.planPrices);
    if (patch.bankAccounts) qc.setQueryData(qk.adminBankAccounts, patch.bankAccounts);
  }, [qc]);
}
